import { z } from 'zod';

export const MONITORING_CHECKS_LIMIT = 'monitoring_checks';
export const MONITORING_SCAN_TARGETS_LIMIT = 'monitoring_scan_targets';
export const MONITORING_UPTIME_FEATURE = 'monitoring_uptime';
export const MONITORING_ASSERTIONS_FEATURE = 'monitoring_advanced_assertions';
export const MONITORING_SECURITY_FEATURE = 'monitoring_security_scanning';
export const MONITORING_CLIENT_FEATURE = 'monitoring_client_visibility';
export const MONITORING_ESCALATION_FEATURE = 'monitoring_alert_escalation';

export const MONITOR_CHECK_KINDS = ['http', 'tcp', 'certificate'] as const;
export type MonitorCheckKind = (typeof MONITOR_CHECK_KINDS)[number];

export const MONITOR_CHECK_STATUSES = [
  'unknown',
  'up',
  'degraded',
  'down',
  'recovering',
  'maintenance',
  'suppressed',
] as const;
export type MonitorCheckStatus = (typeof MONITOR_CHECK_STATUSES)[number];

export const MONITOR_OBSERVATION_STATUSES = [
  'success',
  'failure',
  'maintenance',
  'suppressed',
] as const;
export type MonitorObservationStatus = (typeof MONITOR_OBSERVATION_STATUSES)[number];

export const MONITOR_INCIDENT_STATUSES = ['open', 'resolved'] as const;
export type MonitorIncidentStatus = (typeof MONITOR_INCIDENT_STATUSES)[number];

export const SECURITY_TARGET_KINDS = ['image', 'volume'] as const;
export type SecurityTargetKind = (typeof SECURITY_TARGET_KINDS)[number];

export const SECURITY_SEVERITIES = ['info', 'low', 'medium', 'high', 'critical'] as const;
export type SecuritySeverity = (typeof SECURITY_SEVERITIES)[number];

export const SECURITY_FINDING_KINDS = ['vulnerability', 'malware'] as const;
export type SecurityFindingKind = (typeof SECURITY_FINDING_KINDS)[number];

export const SECURITY_FINDING_STATUSES = [
  'open',
  'false_positive',
  'accepted_risk',
  'resolved',
] as const;
export type SecurityFindingStatus = (typeof SECURITY_FINDING_STATUSES)[number];

export const SECURITY_SCAN_STATUSES = ['running', 'succeeded', 'failed'] as const;
export type SecurityScanStatus = (typeof SECURITY_SCAN_STATUSES)[number];

export const SECURITY_BLOCK_SEVERITIES = ['none', 'critical', 'high'] as const;
export type SecurityBlockSeverity = (typeof SECURITY_BLOCK_SEVERITIES)[number];

export const SECURITY_REMEDIATION_ACTIONS = [
  'quarantine',
  'remove',
  'rewrite',
  'patch_image',
  'rebuild_image',
] as const;
export type SecurityRemediationAction = (typeof SECURITY_REMEDIATION_ACTIONS)[number];

export const SECURITY_REMEDIATION_STATUSES = ['requested', 'approved', 'rejected'] as const;
export type SecurityRemediationStatus = (typeof SECURITY_REMEDIATION_STATUSES)[number];

const safeHttpUrl = z
  .string()
  .url()
  .max(2_000)
  .refine((value) => {
    const url = new URL(value);
    return (
      ['http:', 'https:'].includes(url.protocol)
      && !url.username
      && !url.password
      && !url.search
      && !url.hash
    );
  }, 'Target must be an http(s) URL without embedded credentials, query parameters or fragments');

const hostSchema = z
  .string()
  .trim()
  .min(1)
  .max(253)
  .regex(/^[A-Za-z0-9][A-Za-z0-9.:-]*$/, 'Host contains unsupported characters');

const monitorCheckBaseSchema = z.object({
  subscriptionId: z.string().uuid(),
  name: z.string().trim().min(1).max(120),
  kind: z.enum(MONITOR_CHECK_KINDS),
  target: z.string().trim().min(1).max(2_000),
  port: z.number().int().min(1).max(65_535).nullable().optional(),
  intervalSeconds: z.number().int().min(60).max(86_400).default(300),
  timeoutMs: z.number().int().min(250).max(30_000).default(5_000),
  expectedStatuses: z.array(z.number().int().min(100).max(599)).max(20).default([200]),
  expectedBodyContains: z.string().max(500).nullable().optional(),
  certificateWarnDays: z.number().int().min(1).max(365).default(30),
  failureThreshold: z.number().int().min(1).max(10).default(3),
  recoveryThreshold: z.number().int().min(1).max(10).default(2),
  dependencyCheckId: z.string().uuid().nullable().optional(),
  escalateAfterMinutes: z.number().int().min(0).max(10_080).default(30),
  enabled: z.boolean().default(true),
});

function validateMonitorCheck(
  value: z.infer<typeof monitorCheckBaseSchema>,
  ctx: z.RefinementCtx,
): void {
  if (value.kind === 'http') {
    const parsed = safeHttpUrl.safeParse(value.target);
    if (!parsed.success) {
      ctx.addIssue({
        code: 'custom',
        path: ['target'],
        message: parsed.error.issues[0]?.message ?? 'Invalid URL',
      });
    }
    if (value.port != null) {
      ctx.addIssue({
        code: 'custom',
        path: ['port'],
        message: 'HTTP checks take their port from the URL',
      });
    }
  } else {
    let target = value.target;
    if (value.kind === 'certificate' && value.target.includes('://')) {
      try {
        const url = new URL(value.target);
        const safeCertificateUrl =
          url.protocol === 'https:'
          && !url.username
          && !url.password
          && !url.search
          && !url.hash
          && url.pathname === '/';
        if (!safeCertificateUrl) {
          ctx.addIssue({
            code: 'custom',
            path: ['target'],
            message:
              'Certificate URL must be an https origin without credentials, path, query or fragment',
          });
        }
        target = url.hostname;
      } catch {
        ctx.addIssue({
          code: 'custom',
          path: ['target'],
          message: 'Invalid certificate URL',
        });
        target = '';
      }
    }
    if (!hostSchema.safeParse(target).success) {
      ctx.addIssue({ code: 'custom', path: ['target'], message: 'Invalid host name' });
    }
    if (value.kind === 'tcp' && value.port == null) {
      ctx.addIssue({ code: 'custom', path: ['port'], message: 'TCP checks require a port' });
    }
  }
  if (value.kind !== 'http' && value.expectedBodyContains) {
    ctx.addIssue({
      code: 'custom',
      path: ['expectedBodyContains'],
      message: 'Body assertions apply only to HTTP checks',
    });
  }
}

export const createMonitorCheckSchema =
  monitorCheckBaseSchema.superRefine(validateMonitorCheck);

export const updateMonitorCheckSchema = monitorCheckBaseSchema
  .omit({ subscriptionId: true })
  .partial();

export type CreateMonitorCheckInput = z.input<typeof createMonitorCheckSchema>;
export type UpdateMonitorCheckInput = z.input<typeof updateMonitorCheckSchema>;

export const createMaintenanceWindowSchema = z
  .object({
    subscriptionId: z.string().uuid(),
    checkId: z.string().uuid().nullable().optional(),
    startsAt: z.string().datetime(),
    endsAt: z.string().datetime(),
    reason: z.string().trim().min(1).max(500),
  })
  .refine((value) => new Date(value.endsAt) > new Date(value.startsAt), {
    path: ['endsAt'],
    message: 'Maintenance must end after it starts',
  });

export type CreateMaintenanceWindowInput = z.infer<typeof createMaintenanceWindowSchema>;

const securityTargetBaseSchema = z.object({
  subscriptionId: z.string().uuid(),
  resourceId: z.string().uuid(),
  kind: z.enum(SECURITY_TARGET_KINDS),
  target: z.string().trim().min(1).max(500),
  cadenceHours: z.number().int().min(1).max(720).default(24),
  blockSeverity: z.enum(SECURITY_BLOCK_SEVERITIES).default('critical'),
  enabled: z.boolean().default(true),
});

function validateSecurityTarget(
  value: z.infer<typeof securityTargetBaseSchema>,
  ctx: z.RefinementCtx,
): void {
  const pattern =
    value.kind === 'volume'
      ? /^[A-Za-z0-9][A-Za-z0-9_.-]{0,254}$/
      : /^[A-Za-z0-9][A-Za-z0-9._/:@-]{0,498}$/;
  const [imageName, digest, ...extra] = value.target.split('@');
  const validDigest =
    value.kind !== 'image'
    || (extra.length === 0
      && (digest === undefined || /^sha256:[a-fA-F0-9]{64}$/.test(digest))
      && Boolean(imageName));
  if (!pattern.test(value.target) || !validDigest) {
    ctx.addIssue({
      code: 'custom',
      path: ['target'],
      message: `Invalid ${value.kind} target`,
    });
  }
}

export const createSecurityTargetSchema =
  securityTargetBaseSchema.superRefine(validateSecurityTarget);

export const updateSecurityTargetSchema = securityTargetBaseSchema
  .omit({ subscriptionId: true, resourceId: true, kind: true })
  .partial();

export const classifyFindingSchema = z
  .object({
    status: z.enum(['false_positive', 'accepted_risk', 'open']),
    reason: z.string().trim().min(3).max(1_000),
    expiresAt: z.string().datetime().nullable().optional(),
  })
  .superRefine((value, ctx) => {
    if (value.status !== 'open' && !value.expiresAt) {
      ctx.addIssue({
        code: 'custom',
        path: ['expiresAt'],
        message: 'False-positive and accepted-risk classifications require a review date',
      });
    }
    if (value.expiresAt && new Date(value.expiresAt).getTime() <= Date.now()) {
      ctx.addIssue({
        code: 'custom',
        path: ['expiresAt'],
        message: 'Classification review date must be in the future',
      });
    }
  });

export const requestRemediationSchema = z.object({
  action: z.enum(SECURITY_REMEDIATION_ACTIONS),
  note: z.string().trim().min(10).max(2_000),
});

export const reviewRemediationSchema = z.object({
  decision: z.enum(['approve', 'reject']),
  note: z.string().trim().min(10).max(2_000),
});

export interface MonitorCheckDto {
  id: string;
  subscriptionId: string;
  subscriptionName: string;
  name: string;
  kind: MonitorCheckKind;
  target: string;
  port: number | null;
  intervalSeconds: number;
  timeoutMs: number;
  expectedStatuses: number[];
  expectedBodyContains: string | null;
  certificateWarnDays: number;
  failureThreshold: number;
  recoveryThreshold: number;
  dependencyCheckId: string | null;
  escalateAfterMinutes: number;
  enabled: boolean;
  status: MonitorCheckStatus;
  lastCheckedAt: string | null;
  nextCheckAt: string;
  lastTransitionAt: string | null;
  flappingUntil: string | null;
  lastError: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MonitorObservationDto {
  id: string;
  checkId: string;
  status: MonitorObservationStatus;
  latencyMs: number | null;
  responseStatus: number | null;
  certificateExpiresAt: string | null;
  detail: string | null;
  observedAt: string;
}

export interface MonitorIncidentDto {
  id: string;
  checkId: string;
  subscriptionId: string;
  status: MonitorIncidentStatus;
  summary: string;
  fingerprint: string;
  openedAt: string;
  lastObservedAt: string;
  escalatedAt: string | null;
  resolvedAt: string | null;
  flapping: boolean;
}

export interface MonitorIncidentDetailDto {
  incident: MonitorIncidentDto;
  check: MonitorCheckDto;
  /** Preserved samples around incident opening and recovery, oldest first. */
  incidentObservations: MonitorObservationDto[];
  /** Latest observations for the check, separate from the historical incident. */
  observations: MonitorObservationDto[];
}

export interface MaintenanceWindowDto {
  id: string;
  subscriptionId: string;
  checkId: string | null;
  startsAt: string;
  endsAt: string;
  reason: string;
  createdAt: string;
}

export interface SecurityScanTargetDto {
  id: string;
  subscriptionId: string;
  resourceId: string;
  resourceName: string;
  kind: SecurityTargetKind;
  target: string;
  cadenceHours: number;
  blockSeverity: SecurityBlockSeverity;
  enabled: boolean;
  lastScannedAt: string | null;
  nextScanAt: string;
  lastError: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SecurityScanRunDto {
  id: string;
  targetId: string;
  status: SecurityScanStatus;
  scanner: string;
  databaseUpdatedAt: string | null;
  findingsCount: number;
  error: string | null;
  startedAt: string;
  finishedAt: string | null;
}

export interface SecurityFindingDto {
  id: string;
  targetId: string;
  subscriptionId: string;
  kind: SecurityFindingKind;
  sourceId: string;
  title: string;
  severity: SecuritySeverity;
  location: string | null;
  installedVersion: string | null;
  fixedVersion: string | null;
  status: SecurityFindingStatus;
  statusReason: string | null;
  statusExpiresAt: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
  resolvedAt: string | null;
  runbookHref: string;
  runbookLabel: string;
}

export interface SecurityRemediationDto {
  id: string;
  findingId: string;
  action: SecurityRemediationAction;
  status: SecurityRemediationStatus;
  requestNote: string;
  reviewNote: string | null;
  requestedBy: string | null;
  reviewedBy: string | null;
  restorePointVerifiedAt: string | null;
  createdAt: string;
  reviewedAt: string | null;
  execution: 'manual_only';
}

export interface MonitoringSubscriptionDto {
  customerManagement?: { zones: string[]; checkIds: string[] };
  subscriptionId: string;
  subscriptionName: string;
  clientName: string;
  permissions: {
    manageChecks: boolean;
    manageSecurity: boolean;
    reviewRemediation: boolean;
  };
  resources: Array<{
    id: string;
    name: string;
    kind: 'application' | 'database' | 'service';
  }>;
  checks: MonitorCheckDto[];
  incidents: MonitorIncidentDto[];
  maintenanceWindows: MaintenanceWindowDto[];
  scanTargets: SecurityScanTargetDto[];
  scanRuns: SecurityScanRunDto[];
  findings: SecurityFindingDto[];
  remediation: SecurityRemediationDto[];
}

export interface MonitoringOverviewDto {
  checks: number;
  checksDown: number;
  openIncidents: number;
  openFindings: number;
  failedScans: number;
  subscriptions: Array<{ id: string; name: string; clientName: string }>;
}
